import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { getEntitySubdomain, getEntity, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { getSession, userSelector } from '@opengov/app-core/session/selectors';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import { IEntity } from '@opengov/og-api-ts';
import { FLAG_PRODUCT_STORIES_ACCESS_CONTROL, STORIES_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { storiesMsgs } from '../../locale';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { OrganizeSectionsAndStories, IOrganizeSectionsAndStoriesProps } from '../components';
import {
  fetchCurrentSubjectClaims,
  fetchSubjectPublicationStoryClaim
} from '../api/subjectClaimsApi';
import { navigateToPublicationFolderList, navigateToPublicationView } from '../state/navigation';
import { hasPrivilege } from '../utils';

// Slim selector for Organize route — no server data (publication/folders/stories
// fetched by component hooks). Only app-core, route params, flags, messages, claims, and effect bridges.
const organizeSectionsAndStoriesProps = createStructuredSelector<any, IOrganizeSectionsAndStoriesProps>({
  publicationId: createSelector(getLocation, (location) => location.params?.publicationId ?? ''),
  selectedSectionId: createSelector(getLocation, (location) => location.params?.selectedSectionId ?? ''),
  rootSectionId: createSelector(getLocation, (location) => location.params?.sectionId ?? ''),
  entityId: getNumericalEntityId,
  entityUuid: createSelector(getEntity, (entity) => entity.uuid),
  user: userSelector,
  messages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationView })),
  storyMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.storyList })),
  entitySubdomain: getEntitySubdomain,
  storiesAccessControlEnabled: createSelector(selectFeatureFlags, (flags) =>
    flags.get(FLAG_PRODUCT_STORIES_ACCESS_CONTROL)
  ),
  navigateToPublicationSectionList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId, sectionId) =>
      navigateToPublicationFolderList(entitySubdomain, publicationId, sectionId)
  ),
  navigateToPublicationStoriesList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId) => navigateToPublicationView(entitySubdomain, publicationId)
  )
});

export const organizeSectionsAndStoriesRoute = {
  name: 'OrganizeSectionsAndStories',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Organize', name)),
  selector: organizeSectionsAndStoriesProps,
  component: OrganizeSectionsAndStories,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation, onSuccess, onError) => {
      const { entitySubdomain, publicationId, sectionId: _sectionId } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          let isAdmin = false;
          if (publicationId) {
            const claimResult = await fetchSubjectPublicationStoryClaim({
              subjectId: getSession(state).uuid,
              publicationId,
              entityUuid: entity.uuid,
              stories_access_control_enabled: true
            });
            isAdmin =
              Array.isArray(claimResult?.allClaims) &&
              claimResult.allClaims.includes(STORIES_ADMIN_PRIVILEGE);
          } else {
            isAdmin = hasPrivilege(
              await fetchCurrentSubjectClaims({
                userUuid: getSession(state).uuid,
                entityUuid: entity.uuid
              }),
              STORIES_ADMIN_PRIVILEGE
            );
          }

          if (!isAdmin) {
            return onError(
              'User does not have permission...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
